import { useCallback, useEffect, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'

import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import TextButton from 'LuxKit/Button/TextButton'
import IconButton from 'LuxKit/Button/IconButton'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import AISparkleIcon from 'components/Search/SearchForm/AISparkleIcon'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import LinePenIcon from 'LuxKit/Icons/line/LinePenIcon'
import LineSpinnerIcon from 'LuxKit/Icons/line/LineSpinnerIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import type { TripListRow } from './tripListTypes'
import TripListRowPrice from './TripListRowPrice'
import { TRIP_LIST_GRID_COLUMNS } from './tripListLayout'
import TripListTypeIcon from './TripListTypeIcon'
import TripListCheckboxButton from './TripListCheckboxButton'

const Row = styled.div`
  display: grid;
  grid-template-columns: ${TRIP_LIST_GRID_COLUMNS};
  align-items: center;
  min-height: ${rem(60)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  cursor: pointer;

  & + & {
    border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }

  &:hover {
    background: ${props => props.theme.palette.neutral.surface.lightBackground};
  }

  &.is-selected,
  &.is-selected:hover {
    background: ${props => props.theme.palette.highlight.primary.lightBackground};
  }
`

const Cell = styled.div`
  min-width: 0;
  padding: ${rem(10)} ${rem(8)};

  &:first-child {
    display: flex;
    justify-content: center;
  }
`

const RowCheckboxSlot = styled.div`
  opacity: 0;
  transition: opacity 120ms ease;
  cursor: pointer;

  input {
    cursor: pointer;
  }

  ${Row}:hover &,
  &:focus-within,
  &.isSelected {
    opacity: 1;
  }
`

const NotePlaceholder = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(6)};
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
`

const CompactBookButton = styled(TextButton)`
  min-width: ${rem(64)};
  border-radius: ${props => props.theme.borderRadius.round};
`

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const SpinnerIcon = styled(LineSpinnerIcon)`
  animation: ${spin} 800ms linear infinite;
`

interface Props {
  row: TripListRow
  selected: boolean
  luxPlusEnabled: boolean
  onSelectRow: (rowId: string, checked: boolean) => void
  onRowClick: (row: TripListRow) => void
  onBookRow: (row: TripListRow) => void
  onViewBooking: (row: TripListRow) => void
  onAskAi: (row: TripListRow) => void
  onDelete: (row: TripListRow) => void
  isBookingPending?: boolean
  actionsDisabled?: boolean
}

function TripListRowItem({
  row,
  selected,
  luxPlusEnabled,
  onSelectRow,
  onRowClick,
  onBookRow,
  onViewBooking,
  onAskAi,
  onDelete,
  isBookingPending = false,
  actionsDisabled = false,
}: Props) {
  const intl = useIntl()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuTriggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (actionsDisabled) {
      setMenuOpen(false)
    }
  }, [actionsDisabled])

  const handleRowClick = useCallback(() => {
    onRowClick(row)
  }, [onRowClick, row])

  const handleCheckboxChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onSelectRow(row.id, event.currentTarget.checked)
  }, [onSelectRow, row.id])

  const handleControlClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
  }, [])

  const toggleMenu = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
    if (actionsDisabled) return
    setMenuOpen(prev => !prev)
  }, [actionsDisabled])

  const closeMenu = useCallback(() => {
    setMenuOpen(false)
  }, [])

  const handleAskAi = useCallback(() => {
    if (actionsDisabled) return
    closeMenu()
    onAskAi(row)
  }, [actionsDisabled, closeMenu, onAskAi, row])

  const handleDelete = useCallback(() => {
    if (actionsDisabled) return
    closeMenu()
    onDelete(row)
  }, [actionsDisabled, closeMenu, onDelete, row])

  const handleCtaClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
    if (actionsDisabled || isBookingPending) return
    onBookRow(row)
  }, [actionsDisabled, isBookingPending, onBookRow, row])

  const handleViewBookingClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
    if (actionsDisabled) return
    onViewBooking(row)
  }, [actionsDisabled, onViewBooking, row])

  const addNoteLabel = intl.formatMessage({
    id: 'DYlGIH000SrCINbZ-WbL',
    defaultMessage: 'Add note',
    description: 'Placeholder text for disabled notes cells in the trip planner list view',
  })

  const bookLabel = intl.formatMessage({
    id: 'tumE0yl0JumvbIhBIWAD',
    defaultMessage: 'Book',
    description: 'CTA button label for bookable rows in the trip planner list view',
  })

  const viewBookingLabel = intl.formatMessage({
    id: 'MSXGaLt27wVVLx3diSj_',
    defaultMessage: 'View booking',
    description: 'CTA button label for booked rows in the trip planner list view',
  })

  return (
    <Row onClick={handleRowClick} className={selected ? 'is-selected' : undefined}>
      <Cell onClick={handleControlClick}>
        <RowCheckboxSlot className={selected ? 'isSelected' : undefined}>
          <TripListCheckboxButton
            size="small"
            checked={selected}
            onChange={handleCheckboxChange}
            aria-label={intl.formatMessage(
              {
                id: 'qevuYZDilBy9necmVigz',
                defaultMessage: 'Select {title}',
                description: 'Accessible label for selecting a row in the trip planner list view',
              },
              { title: row.title },
            )}
          />
        </RowCheckboxSlot>
      </Cell>
      <Cell>
        <Group direction="horizontal" gap={12} verticalAlign="center" fullWidth>
          <TripListTypeIcon type={row.rowType} row={row} />
          <Group direction="vertical" gap={2}>
            <BodyText variant="medium" weight="normal" lineClamp={1}>{row.title}</BodyText>
            {row.subtitle && <Caption variant="large" colour="light-foreground-alt" lineClamp={1}>{row.subtitle}</Caption>}
          </Group>
        </Group>
      </Cell>
      <Cell>
        <NotePlaceholder>
          {!row.note && <LinePenIcon size="XS" />}
          <BodyText variant="small" colour={row.note ? 'normal-contrast' : 'light-foreground-alt'} lineClamp={1}>
            {row.note ?? addNoteLabel}
          </BodyText>
        </NotePlaceholder>
      </Cell>
      <Cell>
        <TripListRowPrice price={row.price} luxPlusEnabled={luxPlusEnabled} />
      </Cell>
      <Cell onClick={handleControlClick}>
        {row.isBooked && (
          <CompactBookButton size="small" kind="secondary" onClick={handleViewBookingClick} disabled={actionsDisabled}>
            {viewBookingLabel}
          </CompactBookButton>
        )}
        {!row.isBooked && row.isBookable && (
          <CompactBookButton
            size="small"
            kind="primary"
            onClick={handleCtaClick}
            disabled={actionsDisabled || isBookingPending}
            aria-busy={isBookingPending}
            startIcon={isBookingPending ? <SpinnerIcon size="XS" /> : false}
          >
            {bookLabel}
          </CompactBookButton>
        )}
      </Cell>
      <Cell onClick={handleControlClick}>
        <IconButton
          size="small"
          kind="tertiary"
          onClick={toggleMenu}
          ref={menuTriggerRef}
          disabled={actionsDisabled}
        >
          <LineEllipsisVIcon />
        </IconButton>
        <DropdownList
          size="S"
          open={menuOpen}
          onClose={closeMenu}
          triggerRef={menuTriggerRef}
          anchorRef={menuTriggerRef}
          placement="bottom-end"
          portaled
        >
          <ClickableListItem
            title={intl.formatMessage({
              id: 'G352qlaFJTQXJzpg0Sg2',
              defaultMessage: 'Ask AI',
              description: 'Dropdown menu item for asking AI about a trip planner list row',
            })}
            size="medium"
            startIcon={<AISparkleIcon size="S" />}
            onClick={handleAskAi}
          />
          <ClickableListItem
            title={intl.formatMessage({
              id: 'NQx7k4Q98rLpfRiwQBQK',
              defaultMessage: 'Delete',
              description: 'Dropdown menu item for deleting a trip planner list row',
            })}
            size="medium"
            startIcon={<LineTrashAltIcon size="S" />}
            onClick={handleDelete}
          />
        </DropdownList>
      </Cell>
    </Row>
  )
}

export default TripListRowItem
